import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../services/supabase'
import { Modal } from '../../components/Common/Modal'
import { FormField } from '../../components/Common/FormField'
import { Button } from '../../components/Common/Button'

const TABS = [
  { id: 'details', label: 'Details' },
  { id: 'address', label: 'Address' },
  { id: 'contact', label: 'Contact' },
  { id: 'banking', label: 'Banking' },
  { id: 'attachments', label: 'Attachments' },
]

export function EntityForm({ onSubmit, onClose, initialData, sidebarCollapsed, isDarkMode = false }) {
  const [activeTab, setActiveTab] = useState('details')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showDuplicatePrompt, setShowDuplicatePrompt] = useState(false)
  const [duplicates, setDuplicates] = useState([])
  const [pendingSubmit, setPendingSubmit] = useState(null)
  const [generatingCode, setGeneratingCode] = useState(false)
  const isNew = !initialData?.id

  const [entityData, setEntityData] = useState({
    name: initialData?.name || '',
    type: initialData?.type || '',
    entity_num: initialData?.entity_num || '',
    customer_code: initialData?.customer_code || '',
    registration_number: initialData?.registration_number || '',
    tax_number: initialData?.tax_number || '',
    industry: initialData?.industry || '',
    active: initialData?.active ?? true,
  })

  const [addressData, setAddressData] = useState(initialData?.entity_addresses?.[0] || {
    address_line_1: '',
    address_line_2: '',
    suburb: '',
    city: '',
    postal_code: '',
    country: 'South Africa',
  })

  const [contactData, setContactData] = useState(initialData?.entity_contacts?.[0] || {
    contact_person: '',
    phone: '',
    email: '',
    website: '',
  })

  const [bankingData, setBankingData] = useState(initialData?.entity_banking_details?.[0] || {
    account_name: '',
    account_type: '',
    bank_name: '',
    branch_name: '',
    branch_code: '',
    account_number: '',
    active: true,
  })

  const generateCustomerCode = useCallback(async (name) => {
    const prefix = (name || '').replace(/[^a-zA-Z]/g, '').substring(0, 4).toUpperCase()
    if (!prefix) return
    setGeneratingCode(true)
    try {
      const [{ data: clientCodes }, { data: entityCodes }] = await Promise.all([
        supabase.from('clients').select('customer_code').ilike('customer_code', `${prefix}%`),
        supabase.from('entities').select('customer_code').ilike('customer_code', `${prefix}%`),
      ])
      const all = [
        ...(clientCodes || []).map(c => c.customer_code),
        ...(entityCodes || []).map(e => e.customer_code),
      ].filter(Boolean)
      let maxNum = 0
      all.forEach(code => {
        const num = parseInt(code.slice(prefix.length), 10)
        if (!isNaN(num) && num > maxNum) maxNum = num
      })
      setEntityData(prev => ({ ...prev, customer_code: prefix + String(maxNum + 1).padStart(3, '0') }))
    } finally {
      setGeneratingCode(false)
    }
  }, [])

  // Auto-generate code for new entities when name is typed and code is still blank
  useEffect(() => {
    if (!isNew || entityData.customer_code) return
    const timer = setTimeout(() => {
      if (entityData.name) generateCustomerCode(entityData.name)
    }, 600)
    return () => clearTimeout(timer)
  }, [entityData.name, entityData.customer_code, isNew, generateCustomerCode])

  const handleEntityChange = (e) => {
    const { name, value, type, checked } = e.target
    setEntityData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : (name === 'customer_code' ? value.toUpperCase() : value) }))
  }

  const handleAddressChange = (e) => {
    const { name, value } = e.target
    setAddressData(prev => ({ ...prev, [name]: value }))
  }

  const handleContactChange = (e) => {
    const { name, value } = e.target
    setContactData(prev => ({ ...prev, [name]: value }))
  }

  const handleBankingChange = (e) => {
    const { name, value, type, checked } = e.target
    setBankingData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const checkForDuplicates = async () => {
    if (!entityData.name && !entityData.tax_number) return []

    try {
      let query = supabase.from('entities').select('id, name, tax_number, active')

      // Exclude current entity when editing
      if (initialData?.id) {
        query = query.neq('id', initialData.id)
      }

      const conditions = []
      if (entityData.name) {
        conditions.push(`name.ilike.${entityData.name}`)
      }
      if (entityData.tax_number) {
        conditions.push(`tax_number.eq.${entityData.tax_number}`)
      }

      if (conditions.length > 0) {
        query = query.or(conditions.join(','))
      }

      const { data, error } = await query

      if (error) throw error
      return data || []
    } catch (err) {
      console.error('Duplicate check error:', err)
      return []
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // Validate customer code uniqueness across both tables
      if (entityData.customer_code) {
        const [{ data: existingClients }, { data: existingEntities }] = await Promise.all([
          supabase.from('clients').select('id').eq('customer_code', entityData.customer_code),
          supabase.from('entities').select('id').eq('customer_code', entityData.customer_code).neq('id', initialData?.id || '00000000-0000-0000-0000-000000000000'),
        ])
        if ((existingClients?.length || 0) + (existingEntities?.length || 0) > 0) {
          setError(`Customer code "${entityData.customer_code}" is already in use. Please choose a unique code.`)
          setLoading(false)
          return
        }
      }

      // Only check for duplicates on CREATE, not EDIT
      if (!isEdit) {
        const foundDuplicates = await checkForDuplicates()
        if (foundDuplicates.length > 0) {
          setDuplicates(foundDuplicates)
          setPendingSubmit({ entityData, addressData, contactData, bankingData })
          setShowDuplicatePrompt(true)
          setLoading(false)
          return
        }
      }

      await onSubmit(entityData, addressData, contactData, bankingData)
    } catch (err) {
      setError(err.message)
      setLoading(false)
    }
  }

  const handleProceedAnyway = async () => {
    setShowDuplicatePrompt(false)
    setLoading(true)
    try {
      await onSubmit(pendingSubmit.entityData, pendingSubmit.addressData, pendingSubmit.contactData, pendingSubmit.bankingData)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleCancelDuplicate = () => {
    setShowDuplicatePrompt(false)
    setPendingSubmit(null)
    setDuplicates([])
  }

  const isEdit = Boolean(initialData?.id)

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isEdit ? 'Edit Entity' : 'Create Entity'}
      size="full"
      sidebarCollapsed={sidebarCollapsed}
      isDarkMode={isDarkMode}
      managed
    >
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col gap-4 pb-4 min-h-0">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div style={{ borderBottom: `1px solid ${isDarkMode ? '#475569' : '#e5e7eb'}` }} className="flex gap-2 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 font-semibold text-sm transition whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-b-2 border-sfp-orange text-sfp-orange'
                  : isDarkMode ? 'text-slate-400 hover:text-slate-300' : 'text-gray-600 hover:text-sfp-darkgray'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 min-h-0 overflow-y-auto space-y-4">

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-4">
              <FormField
                isDarkMode={isDarkMode}
                label="Entity Name"
                name="name"
                value={entityData.name}
                onChange={handleEntityChange}
                required
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  isDarkMode={isDarkMode}
                  label="Type"
                  name="type"
                  value={entityData.type}
                  onChange={handleEntityChange}
                  placeholder="e.g., Company, Trust, Partnership"
                />
                <FormField
                  isDarkMode={isDarkMode}
                  label="Entity Number"
                  name="entity_num"
                  value={entityData.entity_num}
                  onChange={handleEntityChange}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                  Customer Code
                </label>
                <div className="flex gap-2">
                  <input
                    name="customer_code"
                    value={entityData.customer_code}
                    onChange={handleEntityChange}
                    placeholder="e.g. EMME001"
                    className="input-field flex-1"
                    style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}
                  />
                  <button
                    type="button"
                    onClick={() => generateCustomerCode(entityData.name)}
                    disabled={generatingCode || !entityData.name}
                    title="Auto-generate from entity name"
                    className="px-3 py-2 rounded text-xs font-semibold transition flex-shrink-0 disabled:opacity-40"
                    style={{
                      backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                      border: `1px solid ${isDarkMode ? '#475569' : '#d1d5db'}`,
                    }}
                  >
                    {generatingCode ? '…' : '↺ Auto'}
                  </button>
                </div>
                <p className="text-xs mt-1" style={{ color: isDarkMode ? '#64748b' : '#9ca3af' }}>
                  Used to match policies. Must be unique across all clients and entities.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  isDarkMode={isDarkMode}
                  label="Registration Number"
                  name="registration_number"
                  value={entityData.registration_number}
                  onChange={handleEntityChange}
                />
                <FormField
                  isDarkMode={isDarkMode}
                  label="Tax Number"
                  name="tax_number"
                  value={entityData.tax_number}
                  onChange={handleEntityChange}
                />
              </div>
              <FormField
                isDarkMode={isDarkMode}
                label="Industry"
                name="industry"
                value={entityData.industry}
                onChange={handleEntityChange}
                placeholder="e.g., Financial Services, Technology"
              />
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="entity_active"
                  name="active"
                  checked={entityData.active}
                  onChange={handleEntityChange}
                  className="w-4 h-4"
                />
                <label htmlFor="entity_active" className="text-sm font-medium" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                  Active
                </label>
              </div>
            </div>
          )}

          {/* Address Tab */}
          {activeTab === 'address' && (
            <div className="space-y-4">
              <FormField
                isDarkMode={isDarkMode}
                label="Address Line 1"
                name="address_line_1"
                value={addressData.address_line_1}
                onChange={handleAddressChange}
              />
              <FormField
                isDarkMode={isDarkMode}
                label="Address Line 2"
                name="address_line_2"
                value={addressData.address_line_2}
                onChange={handleAddressChange}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  isDarkMode={isDarkMode}
                  label="Suburb"
                  name="suburb"
                  value={addressData.suburb}
                  onChange={handleAddressChange}
                />
                <FormField
                  isDarkMode={isDarkMode}
                  label="City"
                  name="city"
                  value={addressData.city}
                  onChange={handleAddressChange}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  isDarkMode={isDarkMode}
                  label="Postal Code"
                  name="postal_code"
                  value={addressData.postal_code}
                  onChange={handleAddressChange}
                />
                <FormField
                  isDarkMode={isDarkMode}
                  label="Country"
                  name="country"
                  value={addressData.country}
                  onChange={handleAddressChange}
                />
              </div>
            </div>
          )}

          {/* Contact Tab */}
          {activeTab === 'contact' && (
            <div className="space-y-4">
              <FormField
                isDarkMode={isDarkMode}
                label="Contact Person"
                name="contact_person"
                value={contactData.contact_person}
                onChange={handleContactChange}
              />
              <FormField
                isDarkMode={isDarkMode}
                label="Phone"
                name="phone"
                value={contactData.phone}
                onChange={handleContactChange}
              />
              <FormField
                isDarkMode={isDarkMode}
                label="Email"
                name="email"
                type="email"
                value={contactData.email}
                onChange={handleContactChange}
              />
              <FormField
                isDarkMode={isDarkMode}
                label="Website"
                name="website"
                value={contactData.website}
                onChange={handleContactChange}
                placeholder="https://..."
              />
            </div>
          )}

          {/* Banking Tab */}
          {activeTab === 'banking' && (
            <div className="space-y-4">
              <FormField
                isDarkMode={isDarkMode}
                label="Account Name"
                name="account_name"
                value={bankingData.account_name}
                onChange={handleBankingChange}
              />
              <FormField
                isDarkMode={isDarkMode}
                label="Account Type"
                name="account_type"
                value={bankingData.account_type}
                onChange={handleBankingChange}
                placeholder="Savings, Cheque, etc."
              />
              <FormField
                isDarkMode={isDarkMode}
                label="Bank Name"
                name="bank_name"
                value={bankingData.bank_name}
                onChange={handleBankingChange}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  isDarkMode={isDarkMode}
                  label="Branch Name"
                  name="branch_name"
                  value={bankingData.branch_name}
                  onChange={handleBankingChange}
                />
                <FormField
                  isDarkMode={isDarkMode}
                  label="Branch Code"
                  name="branch_code"
                  value={bankingData.branch_code}
                  onChange={handleBankingChange}
                />
              </div>
              <FormField
                isDarkMode={isDarkMode}
                label="Account Number"
                name="account_number"
                value={bankingData.account_number}
                onChange={handleBankingChange}
              />
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="banking_active"
                  name="active"
                  checked={bankingData.active}
                  onChange={handleBankingChange}
                  className="w-4 h-4"
                />
                <label htmlFor="banking_active" className="text-sm font-medium" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                  Active
                </label>
              </div>
            </div>
          )}

          {/* Attachments Tab */}
          {activeTab === 'attachments' && (
            <div className="space-y-4 text-center text-gray-500 py-8">
              <p>Document upload functionality coming soon</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ borderTop: `1px solid ${isDarkMode ? '#475569' : '#e5e7eb'}` }} className="flex gap-2 justify-end pt-4 flex-shrink-0">
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Saving...' : 'Save Entity'}
          </Button>
        </div>
      </form>

      {/* Duplicate Warning Modal */}
      {showDuplicatePrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full" style={{ backgroundColor: isDarkMode ? '#1e293b' : '#ffffff' }}>
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-bold" style={{ color: isDarkMode ? '#f1f5f9' : '#333333' }}>
                ⚠️ Duplicate Entities Found
              </h3>
              <p className="text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>
                We found {duplicates.length} existing {duplicates.length === 1 ? 'entity' : 'entities'} that match this name or tax number:
              </p>
              <div className="bg-amber-50 p-3 rounded max-h-48 overflow-y-auto" style={{ backgroundColor: isDarkMode ? '#78350f' : '#fffbeb' }}>
                {duplicates.map(dup => (
                  <div key={dup.id} className="text-xs mb-2 pb-2" style={{ borderBottom: isDarkMode ? '1px solid #92400e' : '1px solid #fbbf24', color: isDarkMode ? '#fed7aa' : '#92400e' }}>
                    <div className="font-semibold">{dup.name}</div>
                    {dup.tax_number && <div>Tax: {dup.tax_number}</div>}
                    <div className="text-xs" style={{ color: isDarkMode ? '#b45309' : '#b45309' }}>
                      {dup.active ? '✓ Active' : '○ Inactive'}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>
                Do you want to create this entity anyway?
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={handleCancelDuplicate}
                  className="px-4 py-2 rounded text-sm font-semibold"
                  style={{ backgroundColor: isDarkMode ? '#334155' : '#f1f5f9', color: isDarkMode ? '#e2e8f0' : '#333333' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleProceedAnyway}
                  disabled={loading}
                  className="px-4 py-2 rounded text-sm font-semibold text-white bg-sfp-orange hover:bg-orange-600 transition disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Anyway'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}
